import {ApolloLink, Observable} from '@apollo/client';
import {getFieldNodes} from 'src/internal/revamp/graphql/documentUtils/entrypoint';
import {createMissingSchemaLink} from 'src/internal/revamp/links/createMissingSchemaLink';

import type {InternalOperation} from 'src/internal/revamp/apollo/types';
import type {TopLevelFieldsToSchemaId} from 'src/internal/revamp/config/types';
import type {SchemaIdToApolloLink} from 'src/internal/revamp/graphql/compositionFragments/splitQueryBySchema';
/**
 * Creates a link that detects which schema a mutation belongs to and
 * executes the mutation against that schema's link.
 * @param schemas The Sail GraphQL config.
 * @returns Apollo link that will handle mutations for any schema.
 */
export default function createMutationSchemaLink(
  schemaIdToApolloLink: SchemaIdToApolloLink,
  topLevelFieldsToSchemaId: TopLevelFieldsToSchemaId['mutations'],
): ApolloLink {
  return new ApolloLink(function mutationSchemaLink(
    operation: InternalOperation,
    forward,
  ) {
    const operationContext = operation.getContext();
    if (operationContext.graphQlOperation?.type !== 'MUTATION') {
      // When used as a terminating link, forward is undefined
      if (forward) {
        return forward(operation);
      }

      throw new Error(
        'Mutation link used as a terminating link was given a non mutation operation. This is a noop.',
      );
    }

    const mutationFields = getFieldNodes(operation.query);

    if (mutationFields.length !== 1) {
      throw new Error(
        'Mutations only support a single field at a time so operations remain atomic',
      );
    }

    const mutationField = mutationFields[0];
    const schemaId =
      topLevelFieldsToSchemaId[mutationField.name.value]?.schemaId;

    const schemaLink =
      schemaIdToApolloLink[schemaId] || createMissingSchemaLink();

    return new Observable((observer) => {
      const subscription = ApolloLink.execute(schemaLink, {
        query: operation.query,
        variables: operation.variables,
        context: operationContext,
      }).subscribe({
        next: (result) => {
          observer.next(result);
        },
        error: (error) => {
          observer.error(error);
        },
      });

      return (): void => subscription.unsubscribe();
    });
  });
}
